/**
 * UnbilledReceiptsPicker (P2P-1)
 *
 * Shown inside Create Bill once a vendor is selected: lists that vendor's
 * confirmed goods receipts that are not yet on any bill, and lets the AP clerk
 * assemble one bill from the selected receipts ("invoice in hand" case). Vendor,
 * currency, lines, quantity and price are pre-filled and linked for 3-way match
 * server-side. Renders nothing when the vendor has no unbilled receipts.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ReceiptText } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { LocalizedCurrencyDisplay, LocalizedDateDisplay } from '../../../shared/components';
import { InfoTooltip, LtrToken } from '../../../shared/components/common';
import { useUnbilledReceipts } from '../hooks/useBills';

interface UnbilledReceiptsPickerProps {
  vendorId: string;
  companyId?: string;
  onBuild: (receiptIds: string[]) => void;
  isBuilding?: boolean;
}

export function UnbilledReceiptsPicker({
  vendorId,
  companyId,
  onBuild,
  isBuilding,
}: UnbilledReceiptsPickerProps) {
  const { t } = useTranslation('accounting');
  const { data, isLoading } = useUnbilledReceipts(vendorId, companyId, !!vendorId);
  const receipts = useMemo(() => data?.items ?? [], [data]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset selection whenever the vendor (and therefore the receipt list) changes.
  useEffect(() => {
    setSelected(new Set());
  }, [vendorId, companyId]);

  if (!vendorId || isLoading || receipts.length === 0) return null;

  const allSelected = selected.size === receipts.length;
  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(receipts.map((r) => r.id)));
  };

  return (
    <div className="rounded-lg border border-info/40 bg-info/5 p-3 space-y-3">
      <div className="flex items-center gap-1.5">
        <ReceiptText className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">
          {t('bills.fromReceipts.title', { count: receipts.length })}
        </h3>
        <InfoTooltip content={t('bills.fromReceipts.description')} />
      </div>

      <div className="flex items-center gap-2 border-b border-border pb-2">
        <Checkbox
          id="unbilled-select-all"
          checked={allSelected}
          onCheckedChange={toggleAll}
        />
        <Label htmlFor="unbilled-select-all" className="text-xs text-muted-foreground cursor-pointer">
          {t('bills.fromReceipts.selectAll')}
        </Label>
      </div>

      <ul className="space-y-1.5 max-h-56 overflow-y-auto">
        {receipts.map((receipt) => {
          // Checkbox is a sibling of the <label> (not nested) so a row click
          // toggles exactly once — nesting a Radix Checkbox in a <label> with
          // onCheckedChange double-fires (STYLE_GUIDE § Radix event handling).
          const checkboxId = `unbilled-receipt-${receipt.id}`;
          return (
            <li key={receipt.id} className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-accent">
              <Checkbox
                id={checkboxId}
                checked={selected.has(receipt.id)}
                onCheckedChange={() => toggle(receipt.id)}
              />
              <label htmlFor={checkboxId} className="flex flex-1 items-center gap-3 min-w-0 cursor-pointer">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span className="truncate"><LtrToken>{receipt.receipt_number}</LtrToken></span>
                    {receipt.po_number && (
                      <span className="text-xs text-muted-foreground truncate">· <LtrToken>{receipt.po_number}</LtrToken></span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <LocalizedDateDisplay dateOnly date={receipt.received_date} />
                    {' · '}
                    {t('bills.fromReceipts.linesCount', { count: receipt.line_count })}
                  </div>
                </div>
                <div className="text-sm font-medium shrink-0">
                  <LocalizedCurrencyDisplay
                    value={receipt.total_cents / 100}
                    currency={receipt.currency_code}
                  />
                </div>
              </label>
            </li>
          );
        })}
      </ul>

      <Button
        type="button"
        className="w-full"
        disabled={selected.size === 0 || isBuilding}
        onClick={() => onBuild(Array.from(selected))}
      >
        {isBuilding ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <ReceiptText className="mr-2 h-4 w-4" />
        )}
        {t('bills.fromReceipts.build', { count: selected.size })}
      </Button>
    </div>
  );
}
